import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { getCompanyById } from '../services/companyService';
import companySubscriptionService from '../services/companySubscriptionService';
import subscriptionAddonService from '../services/subscriptionAddonService';
import { toast } from 'react-hot-toast';
import API_CONFIG from '../../config/api.config';

/**
 * Helper function to get full logo URL
 */
const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return '';
  if (logoUrl.startsWith('/api')) {
    const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    return `${baseUrl}${logoUrl}`;
  }
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }
  const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
  return `${baseUrl}/api${logoUrl.startsWith('/') ? logoUrl : '/' + logoUrl}`;
};

/**
 * Activate Subscription Page
 * Allows super admin to activate subscription for a company
 */
const ActivateSubscriptionPage = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    billingCycle: 'monthly',
    amountPaid: '',
    currency: 'INR',
    paymentStatus: 'paid',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentReference: '',
    notes: '',
  });

  const [addons, setAddons] = useState([]);
  const [addonDefinitions, setAddonDefinitions] = useState([]);
  const [addonDialogOpen, setAddonDialogOpen] = useState(false);
  const [addonForm, setAddonForm] = useState({ addonDefinitionId: '', quantityUnits: 1, durationType: 'REMAINING', notes: '' });
  const [addonPreview, setAddonPreview] = useState(null);
  const [savingAddon, setSavingAddon] = useState(false);
  const [loadingAddons, setLoadingAddons] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId && !loading) {
      loadAddons();
      loadAddonDefinitions();
    }
  }, [companyId, loading]);

  const loadAddons = async () => {
    try {
      setLoadingAddons(true);
      const data = await subscriptionAddonService.listCompanyAddons(parseInt(companyId));
      setAddons(Array.isArray(data) ? data : []);
    } catch (err) {
      setAddons([]);
    } finally {
      setLoadingAddons(false);
    }
  };

  const loadAddonDefinitions = async () => {
    try {
      const data = await subscriptionAddonService.listActiveAddonDefinitions();
      setAddonDefinitions(Array.isArray(data) ? data : []);
    } catch (err) {
      setAddonDefinitions([]);
    }
  };

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch company details
      const companyData = await getCompanyById(parseInt(companyId));
      setCompany(companyData);
      
      // Try to fetch subscription details (if endpoint exists)
      try {
        const subscriptionData = await companySubscriptionService.getCompanySubscription(parseInt(companyId));
        setSubscription(subscriptionData);
      } catch (err) {
        // Subscription might not exist yet, that's okay
        console.log('Subscription not found, will be created on activation');
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError(err.message || 'Failed to load company data');
      toast.error(err.message || 'Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amountPaid || parseFloat(formData.amountPaid) <= 0) {
      toast.error('Please enter a valid amount paid');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const activationData = {
        companyId: parseInt(companyId),
        billingCycle: formData.billingCycle,
        amountPaid: parseFloat(formData.amountPaid),
        currency: formData.currency,
        paymentStatus: formData.paymentStatus,
        paymentDate: formData.paymentDate ? new Date(formData.paymentDate).toISOString() : null,
        paymentReference: formData.paymentReference || null,
        notes: formData.notes || null,
      };

      const response = await companySubscriptionService.activateSubscription(activationData);
      
      // Show success message
      const successMsg = response.message || `Subscription activated successfully for ${company.name}`;
      setSuccessMessage(successMsg);
      toast.success(successMsg);
      
      // Navigate after a short delay to allow user to see the success message
      setTimeout(() => {
        navigate('/superadmin/clients');
      }, 2000);
    } catch (err) {
      console.error('Error activating subscription:', err);
      setError(err.message || 'Failed to activate subscription');
      toast.error(err.message || 'Failed to activate subscription');
    } finally {
      setSaving(false);
    }
  };

  const formatAddonDate = (dateVal) => {
    if (!dateVal) return '-';
    try {
      const date = Array.isArray(dateVal) && dateVal.length >= 3
        ? new Date(dateVal[0], (dateVal[1] || 1) - 1, dateVal[2])
        : typeof dateVal === 'string' ? new Date(dateVal.replace(' ', 'T')) : new Date(dateVal);
      return date.toLocaleDateString();
    } catch (e) {
      return '-';
    }
  };

  const formatCurrency = (amount, currency) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', minimumFractionDigits: 2 }).format(amount);
  };

  const handleOpenAddAddon = () => {
    setAddonForm({ addonDefinitionId: addonDefinitions[0]?.id || '', quantityUnits: 1, durationType: 'REMAINING', notes: '' });
    setAddonPreview(null);
    setAddonDialogOpen(true);
  };

  const handleAddonPreview = async () => {
    const id = addonForm.addonDefinitionId ? parseInt(addonForm.addonDefinitionId, 10) : null;
    if (!id) {
      toast.error('Select an add-on type');
      return;
    }
    try {
      const preview = await subscriptionAddonService.previewAddonPrice(
        parseInt(companyId),
        id,
        parseInt(addonForm.quantityUnits, 10) || 1,
        addonForm.durationType || 'REMAINING'
      );
      setAddonPreview(preview);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Preview failed');
      setAddonPreview(null);
    }
  };

  const handleAddAddonSave = async () => {
    const id = addonForm.addonDefinitionId ? parseInt(addonForm.addonDefinitionId, 10) : null;
    if (!id) {
      toast.error('Select an add-on type');
      return;
    }
    try {
      setSavingAddon(true);
      await subscriptionAddonService.addCompanyAddon({
        companyId: parseInt(companyId),
        addonDefinitionId: id,
        quantityUnits: parseInt(addonForm.quantityUnits, 10) || 1,
        durationType: addonForm.durationType || 'REMAINING',
        notes: addonForm.notes || null,
      });
      toast.success('Add-on added');
      setAddonDialogOpen(false);
      loadAddons();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to add add-on');
    } finally {
      setSavingAddon(false);
    }
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={40} />
        </Box>
      </SuperAdminLayout>
    );
  }

  if (!company) {
    return (
      <SuperAdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Company not found</Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/superadmin/clients')} sx={{ mt: 2 }}>
            Back to Clients
          </Button>
        </Box>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/superadmin/clients')}
            sx={{ color: '#2c3e50' }}
          >
            Back to Clients
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Activate Subscription
          </Typography>
        </Box>

        {/* Company Info Card */}
        <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                src={getLogoUrl(company.logoUrl)}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: '#5a9bd4',
                  color: '#fff',
                }}
              >
                {company.name?.charAt(0)?.toUpperCase() || 'C'}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  {company.name}
                </Typography>
                {company.email && (
                  <Typography variant="body2" color="text.secondary">
                    {company.email}
                  </Typography>
                )}
              </Box>
            </Box>
            {subscription && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Current Plan: <strong>{subscription.planName || 'N/A'}</strong>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Success Alert */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Activation Form */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
            Subscription Details
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Billing Cycle */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Billing Cycle</InputLabel>
                  <Select
                    value={formData.billingCycle}
                    label="Billing Cycle"
                    onChange={handleInputChange('billingCycle')}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Amount Paid */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Amount Paid"
                  type="number"
                  value={formData.amountPaid}
                  onChange={handleInputChange('amountPaid')}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Currency */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency}
                    label="Currency"
                    onChange={handleInputChange('currency')}
                  >
                    <MenuItem value="INR">INR (Indian Rupee)</MenuItem>
                    <MenuItem value="USD">USD (US Dollar)</MenuItem>
                    <MenuItem value="EUR">EUR (Euro)</MenuItem>
                    <MenuItem value="GBP">GBP (British Pound)</MenuItem>
                    <MenuItem value="SGD">SGD (Singapore Dollar)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Payment Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    value={formData.paymentStatus}
                    label="Payment Status"
                    onChange={handleInputChange('paymentStatus')}
                  >
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Payment Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Payment Date"
                  type="date"
                  value={formData.paymentDate}
                  onChange={handleInputChange('paymentDate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Payment Reference */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Reference"
                  value={formData.paymentReference}
                  onChange={handleInputChange('paymentReference')}
                  placeholder="Transaction ID, Check Number, etc."
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  placeholder="Additional notes or comments..."
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/superadmin/clients')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                    sx={{
                      bgcolor: '#5a9bd4',
                      '&:hover': {
                        bgcolor: '#4a8bc4',
                      },
                    }}
                  >
                    {saving ? 'Activating...' : 'Activate Subscription'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Add-ons for this company */}
        {subscription && (
          <Card elevation={2} sx={{ mt: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  Add-ons
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddAddon}
                  sx={{ borderColor: '#5a9bd4', color: '#5a9bd4', '&:hover': { borderColor: '#4a8bc4', bgcolor: 'rgba(90,155,212,0.08)' } }}
                >
                  Add add-on
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Extra users, locations, or products for this company. Add-on can run until plan end (pro-rated) or for a full period (full price).
              </Typography>
              {loadingAddons ? (
                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : addons.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No add-ons. Click &quot;Add add-on&quot; to add one.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Add-on</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Valid until</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {addons.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.addonDefinitionName}</TableCell>
                          <TableCell>{row.quantityUnits} x {row.quantityPerUnit || 1}</TableCell>
                          <TableCell>{row.durationType === 'FULL_PERIOD' ? 'Full period' : 'Until plan end'}</TableCell>
                          <TableCell>{formatAddonDate(row.effectiveUntil)}</TableCell>
                          <TableCell>{formatCurrency(row.amountPaid, row.currency)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add add-on dialog */}
        <Dialog open={addonDialogOpen} onClose={() => !savingAddon && setAddonDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add add-on</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Add-on type</InputLabel>
                  <Select
                    value={addonForm.addonDefinitionId}
                    label="Add-on type"
                    onChange={(e) => setAddonForm((p) => ({ ...p, addonDefinitionId: e.target.value }))}
                  >
                    {addonDefinitions.map((d) => (
                      <MenuItem key={d.id} value={d.id}>{d.name} ({d.metricType}) – {formatCurrency(d.priceYearly, d.currency)}/year</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Quantity"
                  value={addonForm.quantityUnits}
                  onChange={(e) => setAddonForm((p) => ({ ...p, quantityUnits: e.target.value }))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={addonForm.durationType}
                    label="Duration"
                    onChange={(e) => { setAddonForm((p) => ({ ...p, durationType: e.target.value })); setAddonPreview(null); }}
                  >
                    <MenuItem value="REMAINING">Until plan end (pro-rated)</MenuItem>
                    <MenuItem value="FULL_PERIOD">Full period (full price)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  value={addonForm.notes}
                  onChange={(e) => setAddonForm((p) => ({ ...p, notes: e.target.value }))}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" size="small" onClick={handleAddonPreview}>Preview price</Button>
                {addonPreview && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>Amount to charge:</strong> {formatCurrency(addonPreview.amountToCharge, addonPreview.currency)}</Typography>
                    <Typography variant="body2" color="text.secondary">Valid until: {formatAddonDate(addonPreview.effectiveUntil)} ({addonPreview.monthsCovered} months)</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAddonDialogOpen(false)} disabled={savingAddon}>Cancel</Button>
            <Button variant="contained" onClick={handleAddAddonSave} disabled={savingAddon} sx={{ bgcolor: '#5a9bd4', '&:hover': { bgcolor: '#4a8bc4' } }}>
              {savingAddon ? 'Saving...' : 'Add add-on'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
};

export default ActivateSubscriptionPage;

